import React, { memo, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import CurrencyInput from "react-currency-input";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";

import { Creators as NotificationsActions } from "../../store/ducks/notifications";

import { isScopes } from "../../services/Auth";
import { maskCPF } from "../../utils/CPF";
import { maskCNPJ } from "../../utils/CNPJ";
import { isValidForm } from "../../utils/Form";
import { isEmpty, ucFirstAll } from "../../utils/String";
import { clearObject, mergeObject } from "../../utils/Object";

import ClientService from "../../services/Client";
import ClientSchemaReduced from "../../services/Client/SchemaReduced";
import SupportPointService from "../../services/SupportPoint";
import SupportPointSchema from "../../services/SupportPoint/Schema";

import SupportPointMap from "./SupportPointMap";

const SupportPoint = ({
  options,
  props: { currentUser },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const clientService = new ClientService();
  const supportPointService = new SupportPointService();

  const {
    supportPoint: __supportPoint = {},
    close: __close = () => {}
  } = options;

  const [supportPoint, setSupportPoint] = useState(mergeObject(JSON.parse(JSON.stringify(SupportPointSchema)), __supportPoint));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:supportPoints:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setSupportPoint(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [clientSelectedOption, setClientSelectedOption] = useState(supportPoint.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), supportPoint.client),
    label: supportPoint.client.type === "LEG" ? (`${supportPoint.client.legal.socialReason} | CNPJ: ${maskCNPJ(supportPoint.client.legal.cnpj)}`) : (`${supportPoint.client.personal.name} | CPF: ${maskCPF(supportPoint.client.personal.cpf)}`),
    value: supportPoint.client.id
  });

  const [globalOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [typeOptions] = useState([
    { value: "CHE", label: t("Title.SupportPoint.CHE") },
    { value: "COL", label: t("Title.SupportPoint.COL") },
    { value: "SUP", label: t("Title.SupportPoint.SUP") }
  ]);

  const [running, setRunning] = useState(false);

  const buttonDisabled = () => {
    if (!running) return false;
    return true;
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : `${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`,
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if(!running) {
      setRunning(true);
      let { valid, message } = isValidForm(e);
      if(!valid) {
        if(!message) {
          message = t("Error.Fields.Empty");
        }
        addNotice({
          title: t("Title.SupportPoint"),
          content: message
        });
      }
      else {
        let response = false;
        if(isCreate()) {
          response = await supportPointService.create({
            data: handleSubmitForm({ supportPoint }),
            token: currentUser.token
          });
        }
        else {
          response = await supportPointService.update({
            id: supportPoint.id,
            data: handleSubmitForm({ supportPoint }),
            token: currentUser.token
          });
        }

        if(response.success) {
          addNotice({
            title: t("Title.SupportPoint"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.SupportPoint"),
            content: t(`Error.SupportPoint.${response.error.type}.${ucFirstAll(response.error.details[0].path)}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ supportPoint }) => {
    let newSupportPoint = Object.assign({}, JSON.parse(JSON.stringify(supportPoint)));
    newSupportPoint = clearObject({ data: newSupportPoint });
    newSupportPoint.id = undefined;
    return newSupportPoint;
  };

  const inputDisabled = () => {
    if (!running) return false;
    return true;
  };

  const isCreate = () => {
    return supportPoint.id === "";
  };

  return (
    <Form id="forms-support-point" noValidate onSubmit={handleSubmit}>
      <Card>
        <Card.Header>{t("Title.GeneralData")}</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-support-point-active">
                <Form.Label>{t("Label.Active")}:</Form.Label>
                <Select
                  className="menu-outer-bottom"
                  classNamePrefix="select"
                  isDisabled={inputDisabled()}
                  onChange={e => {
                    const active = e.value;
                    setSupportPoint(prevState => ({
                      ...prevState,
                      active
                    }));
                  }}
                  options={activeOptions}
                  value={activeOptions.find(x => x.value === supportPoint.active)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-user-client">
                <Form.Label>{t("Title.Client")}:</Form.Label>
                <AsyncSelect
                  className={`menu-outer-bottom ${!isEmpty(supportPoint.client.id) ? "is-valid" : "is-invalid"}`}
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  isDisabled={inputDisabled() || !isScopes({
                    currentUser,
                    scopes: [
                      "is:master",
                      "read:supportPoints:all"
                    ],
                    every: false
                  })}
                  loadOptions={clientLoadOptions}
                  loadingMessage={() => t("React.Select.Wait")}
                  noOptionsMessage={() => t("React.Select.NoOptions")}
                  onChange={e => {
                    if(e === null) {
                      setClientSelectedOption(e);
                      setSupportPoint(prevState => ({
                        ...prevState,
                        client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                      }));
                    }
                    else {
                      const { dataAux, label, value } = e;
                      setClientSelectedOption({ dataAux, label, value });
                      setSupportPoint(prevState => ({
                        ...prevState,
                        client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                      }));
                    }
                  }}
                  placeholder={t("Title.Client.Select")}
                  value={clientSelectedOption}
                  required={true}
                />
                <Form.Control.Feedback className={`${!isEmpty(supportPoint.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          {
              isScopes({
                currentUser,
                scopes: [
                  "is:master",
                  "read:supportPoints:all"
                ],
                every: false
              }) ? (
                <Row>
                  <Col>
                    <Form.Group controlId="forms-support-point-global">
                      <Form.Label>{t("Label.Global")}:</Form.Label>
                      <Select
                        className="menu-outer-bottom"
                        classNamePrefix="select"
                        isDisabled={inputDisabled()}
                        onChange={e => {
                          const global = e.value;
                          setSupportPoint(prevState => ({
                            ...prevState,
                            global
                          }));
                        }}
                        options={globalOptions}
                        value={globalOptions.find(x => x.value === supportPoint.global)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              ) : null
            }
          <Row>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-support-point-name">
                <Form.Label>{t("Label.Name")}:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("Placeholder.Name")}
                  disabled={inputDisabled()}
                  onChange={e => {
                    const name = e.target.value;
                    setSupportPoint(prevState => ({
                      ...prevState,
                      name: name.toUpperCase()
                    }));
                  }}
                  autoComplete="off"
                  value={supportPoint.name}
                  isValid={!isEmpty(supportPoint.name)}
                  isInvalid={isEmpty(supportPoint.name)}
                  required={true}
                />
                <Form.Control.Feedback type="invalid">{t("Feedback.Name")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-support-point-range">
                <Form.Label>{t("Label.Range")}:</Form.Label>
                <CurrencyInput
                  id="forms-support-point-range"
                  className={"form-control"}
                  type="text"
                  placeholder={t("Placeholder.Range")}
                  disabled={inputDisabled()}
                  decimalSeparator="."
                  thousandSeparator=""
                  precision="0"
                  prefix=""
                  value={supportPoint.range}
                  onChangeEvent={e => {
                    const range = e.target.value;
                    setSupportPoint(prevState => ({
                      ...prevState,
                      range
                    }));
                  }}
                />
                <Form.Control.Feedback type="invalid">{t("Feedback.Range")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Form.Group controlId="forms-support-point-type">
                <Form.Label>{t("Label.Type")}:</Form.Label>
                <Select
                  className="menu-outer-bottom"
                  classNamePrefix="select"
                  isDisabled={inputDisabled()}
                  onChange={e => {
                    const type = e.value;
                    setSupportPoint(prevState => ({
                      ...prevState,
                      type
                    }));
                  }}
                  options={typeOptions}
                  value={typeOptions.find(x => x.value === supportPoint.type)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <SupportPointMap
                locationOptions={{
                  point: supportPoint.location,
                  range: Number(supportPoint.range),
                  supportPoint,
                  setSupportPoint: (supportPoint) => setSupportPoint(supportPoint)
                }
              } />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(SupportPoint));
